import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import elementsActions from 'actions/editor/elements';
import FeedbackCriteria from 'components/FeedbackCriteria/FeedbackCriteria';
import { LabeledCheckbox } from 'components/shared/checkbox';
import TextArea from 'components/shared/debouncedTextArea';
import { hasGlobalFeature } from 'selectors';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import AddCriteria from 'components/FeedbackCriteria/AddCriteria';

import {
  TabList,
  TabbedView,
  Tab,
  TabPanel,
} from 'components/shared/TabbedView';
import ElementBase from '../base';

const RolePlay = ({
  content,
  handleUpdate,
  handleCriteriaUpdate,
  applyChanges,
  hasAnonymousLogin,
}) => {
  const onChange = value => {
    applyChanges(value);
    handleUpdate(value);
  };

  const [tabIndex, setTabIndex] = useState(0);
  const selectedCriteria = content.criteria;

  return (
    <>
      <div className="RolePlayElement">
        <div className="EditorElement-body border-1 border-solid border-top-0 border-gray-light w-100%">
          <div className="EditorElement-header border-bottom-0 border-left-0 border-right-0 d-f fd-c ai-b pl-32 pr-32 pt-32">
            <div className="d-f jc-sb w-100p ai-fe">
              <Text variant={TEXT_VARIANTS.SUBHEADER}>
                {t('lessons.editor.elements.role_play_title')}
              </Text>
              <LabeledCheckbox
                className=" RolePlayOptions-container fz-12 w-fc"
                key={`${content.id}_required`}
                label={t('practice.role_play.labels.required_partner')}
                checked={content.required}
                onChange={e => onChange({ required: e.target.checked })}
              />
            </div>
            <Text
              variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}
              className="mb-16 mt-8"
            >
              {t('practice.role_play.explanation')}
            </Text>
          </div>
          <TabbedView onSelect={setTabIndex} selectedIndex={tabIndex}>
            <TabList className="mb-24 pl-32">
              <Tab>{t('questions.answer_key.tabs.practice_details')}</Tab>
              <Tab>{t('questions.answer_key.tabs.partner_resources')}</Tab>
            </TabList>
            <TabPanel>
              <>
                <div className="RolePlay-container">
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('practice.role_play.labels.title')}
                  </Text>
                  <TextArea
                    placeholder={t('practice.role_play.titles.role_play')}
                    className="RolePlay-textbox fz-16 Animated mb-24 mt-12 llui-text llui-theme-text llui-text-title-bold"
                    onChange={e => onChange({ title: e })}
                    value={content.title}
                    singleLine
                  />
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('practice.role_play.titles.scenario')}
                    {<span className="c-danger-red"> * </span>}
                  </Text>
                  <TextArea
                    placeholder={t('practice.role_play.placeholders.scenario')}
                    className="RolePlay-textbox fz-16 Animated mb-24 mt-12"
                    onChange={e => onChange({ scenario: e })}
                    value={content.scenario}
                  />
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('practice.role_play.titles.goal')}
                    {<span className="c-danger-red"> *</span>}
                  </Text>
                  <TextArea
                    placeholder={t('practice.role_play.placeholders.goal')}
                    className="RolePlay-textbox fz-16 mt-12 mb-24"
                    onChange={e => onChange({ goal: e })}
                    value={content.goal}
                  />
                </div>
                <div className="bg-teal-lightest w-100% h-50 pl-32 mb-20 p-16">
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('practice.role_play.titles.learner')}
                  </Text>
                </div>
                <div className="RolePlay-container pb-32">
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('practice.role_play.titles.partner')}
                    {<span className="c-danger-red"> * </span>}
                  </Text>
                  <TextArea
                    placeholder={t('practice.role_play.placeholders.partner')}
                    className="RolePlay-textbox fz-16 Animated mt-12"
                    onChange={e => onChange({ partner: e })}
                    value={content.partner}
                  />
                  {hasAnonymousLogin && (
                    <Text
                      variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}
                      className="d-ib mt-2 mb-4"
                    >
                      {t(
                        'practice.role_play.disclaimers.partners_need_account',
                      )}
                    </Text>
                  )}
                </div>
                <div>
                  <div className="bg-teal-lightest w-100% h-50 pl-32 mb-32 p-16">
                    <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                      {t('practice.role_play.titles.partner_sees')}
                    </Text>
                  </div>
                  <div className="RolePlay-container mb-24">
                    <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                      {t('practice.role_play.titles.partner_role')}
                      {<span className="c-danger-red"> *</span>}
                    </Text>
                    <TextArea
                      placeholder={t(
                        'practice.role_play.placeholders.partner_role',
                      )}
                      className="RolePlay-textbox fz-16  Animated mb-32 mt-12"
                      onChange={e => onChange({ partner_role: e })}
                      value={content.partner_role}
                    />
                    <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                      {t('practice.role_play.titles.additional_context')}
                    </Text>
                    <TextArea
                      placeholder={t(
                        'practice.role_play.placeholders.additional_context',
                      )}
                      className="RolePlay-textbox fz-16  Animated mb-32 mt-12"
                      onChange={e => onChange({ additional_context: e })}
                      value={content.additional_context}
                    />
                  </div>
                  <div className="d-f w-100% jc-sb ml-37">
                    <a
                      className="pb-24 pr-35"
                      href={`/practice/preview/role_play_guides/${content.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Text variant={TEXT_VARIANTS.BODY_LINK} className="td-n">
                        {t('practice.role_play.partner_guide.preview_guide')}
                      </Text>
                    </a>
                  </div>
                </div>
              </>
            </TabPanel>
            <TabPanel>
              {selectedCriteria.length > 0 ? (
                <div className="ml-32 mr-32 mb-32">
                  <FeedbackCriteria
                    selectedCriteria={selectedCriteria}
                    handleUpdate={handleCriteriaUpdate}
                    canUseCustomFeedbackCriteria
                  />
                </div>
              ) : (
                <div className="ml-32 mb-32">
                  <AddCriteria
                    handleUpdate={handleCriteriaUpdate}
                    canUseCustomFeedbackCriteria
                  />
                </div>
              )}
            </TabPanel>
          </TabbedView>
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch, { id }) => ({
  handleCriteriaUpdate(changes) {
    dispatch(elementsActions.updateCriteria(id, changes));
  },
});

const mapStateToProps = state => ({
  hasAnonymousLogin: hasGlobalFeature(state, 'anonymous_login'),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  ElementBase,
)(RolePlay);
